import { ObjUtils, Iterable, IComparable } from "../Utils/ObjUtils";
import { List } from "../Utils/List";
import { Device } from "./Device";
import { NetworkDiffer, MatchResult, DeviceIdentityAttrs, MatchingDevice } from "./NetworkDiffer";
import { NetworkDiffSettings } from "./NetworkDiffSettings";
import { DeviceSet, SerialNumberInfo, NetBiosNameInfo, SystemNameInfo, HostNameInfo } from "./DeviceSet";
import { StringUtils } from "../Utils/StringUtils";
import { DependentDeviceId } from "./DependentDeviceId";
import { IpAddress, IpVersion } from "./IpAddress";
import { MacAddress } from "./MacAddress";
import { IfTable, IfEntry } from "../InfoSets/IfTable";
import { VirtualPort, VirtualPortTable, VirtualPortGroup, VirtualPortGroupTable } from "./VirtualPortGroupTable";
import { PhysicalNic, PhysicalNicTable } from "./PhysicalNicTable";
import { VirtualNic, VirtualNicTable } from "./VirtualNicTable";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";

export class KeyPair {
	public Name: string;
	public Value: string;

	constructor(name: string, value: string) {
		this.Name = name;
		this.Value = value;
	}

	public Equals(obj: any): boolean {
		if (obj == null)
			return false;

		if (obj == this)
			return true;

		if (!(obj instanceof KeyPair))
			return false;

		var other = <KeyPair>obj;

		return (this.Name == other.Name && this.Value == other.Value);
	}

	public GetHashCode(): number {
		return ObjUtils.GetHashCode(this.Name) ^ ObjUtils.GetHashCode(this.Value);
    }
}

export class Key {

	private _Pairs: List<KeyPair>;

	constructor(other?: Key) {
		this._Pairs = new List<KeyPair>();

		if (other)
			this.Copy(other);
	}

    protected static FormatName(nameType: string, nameIndex: number) {
        return nameType + "[" + nameIndex + "]";
    }

	public Add(pair: KeyPair) {
		this._Pairs.Add(pair);
	}

	public Find(name: string): KeyPair {
		var foundPair: KeyPair = null;
		this._Pairs.Some((pair) => {
			if (pair.Name == name) {
				foundPair = pair;
				return true;
			}
		});
		return foundPair;
	}

    private FindIndex(name: string): number {
        for (var i: number = 0; i < this._Pairs.Count; ++i)
        {
            if (this._Pairs.Get(i).Name == name)
                return i;
        }
        return -1;
    }

    public Remove(name: string): boolean {
        var index: number = this.FindIndex(name);
        if (index >= 0) {
            this._Pairs.RemoveAt(index);
            return true;
        }
        else {
            return false;
        }
    }

	public Copy(other: Key) {
		other._Pairs.ForEach((pair) => {
			this.Add(new KeyPair(pair.Name, pair.Value));
		});
	}

	public Equals(obj: any): boolean {
		if (obj == null)
			return false;

		if (obj == this)
			return true;

		if (!(obj instanceof Key))
			return false;

		var other = <Key>obj;

		if (this._Pairs.Count != other._Pairs.Count)
			return false;

		for (var i = 0; i < this._Pairs.Count; ++i) {
			if (!this._Pairs.Get(i).Equals(other._Pairs.Get(i))) {
				return false;
			}
		}

		return true;
	}

	public GetHashCode(): number {
		var result = 0;

		this._Pairs.ForEach((pair) => {
			result ^= pair.GetHashCode();
		});

		return result;
	}

    public ToString(): string {
        var result: string = "";

        this._Pairs.ForEach((pair) => {
            result += "[" + pair.Name + "=" + pair.Value + "]";
        }, this);

        return result;
    }

    public toString() {
        return this.ToString();
    }

	public ForEach(callbackfn: (value: KeyPair, index: number, array: KeyPair[]) => void, thisArg?: any): void {
		this._Pairs.ForEach(callbackfn, thisArg);
	}

	public Some(callbackfn: (value: KeyPair, index: number, array: KeyPair[]) => boolean, thisArg?: any): boolean {
		return this._Pairs.Some(callbackfn, thisArg);
	}

	public Every(callbackfn: (value: KeyPair, index: number, array: KeyPair[]) => boolean, thisArg?: any): boolean {
		return this._Pairs.Every(callbackfn, thisArg);
    }

	protected _Write(obj: any, objSerName: string, context: any): void {
		obj[objSerName] = {};
		obj = obj[objSerName];

		obj.entries = [];
		ObjSerUtils.WriteCollection(obj.entries, this._Pairs, (keyPair: KeyPair) => {
			return {
				entry: {
					name: keyPair.Name,
					value: keyPair.Value
				}
			};
		}, context);
	}

	protected _Read(obj: any, objSerName: string, context: any): void {
		if (!obj[objSerName])
			throw new Error();
		obj = obj[objSerName];

		if (obj.entries !== undefined) {
			ObjSerUtils.ReadCollection(obj.entries, this._Pairs, (elem: any) => {
				return new KeyPair(elem.entry.name, elem.entry.value);
			}, context);
		}
	}
}

export class KeyNames {
	public static Ip = "ip";
	public static Mac = "mac";
	public static NbName = "nb-name";
	public static NbDomain = "nb-domain";
	public static SysName = "sys-name";
	public static SysOid = "sys-oid";
	public static HostName = "host-name";
	public static SerialNumber = "ser-num";
	public static ControllerLabel = "ctrl-label";
    public static IfIndex = "if-idx";
    public static IfName = "if-name";
}

export class DeviceKey extends Key implements ObjectSerializer {

    private static ControllerKeyPrefix: string = "c-";

    public static Create(device: Device): DeviceKey {
		return new DeviceKey(device);
    }

    /// <summary>
    /// Decides whether two device keys are matching (i.e., they probably refer to the same
    /// device according to the NetworkDiffer's device matching algorithm)
    /// </summary>
    /// <param name="a">First DeviceKey to be matched</param>
    /// <param name="b">Second DeviceKey to be matched</param>
    /// <returns></returns>
    public static IsMatch(a: DeviceKey, b: DeviceKey): boolean
	{
        var aDevIdAttrs: DeviceIdentityAttrs  = a.ExtractDeviceIdentityAttrs();
        var bDevIdAttrs: DeviceIdentityAttrs  = b.ExtractDeviceIdentityAttrs();

		let ThresholdScore = 1;
		var matchResult: MatchResult = NetworkDiffer.IsMatch(aDevIdAttrs, bDevIdAttrs, ThresholdScore);
        return matchResult.IsMatch;
    }

	public static CreateFromKey(other: Key): DeviceKey {
		var result = new DeviceKey();
		result.Copy(other);
		return result;
    }

	constructor(device?: Device) {
		super();

        if (device) {
            var i: number = 0;
			var name: string = "";

			var devIdAttrs = new DeviceIdentityAttrs(device);

            if (devIdAttrs.HasDependentDeviceId) {
                // Copy in controller key attributes
                devIdAttrs.DependentDeviceId.ControllerKey.ForEach((pair) => {
                    // Add controller key prefix to attribute name
                    var convertedName: string = DeviceKey.ControllerKeyPrefix + pair.Name;
                    var value: string = pair.Value;

                    // Add controller key attribute to this key
                    this.Add(new KeyPair(convertedName, value));
                }, this);

                // Add controller label to this key
                name = Key.FormatName(KeyNames.ControllerLabel, 1);
                this.Add(new KeyPair(name, devIdAttrs.DependentDeviceId.ControllerLabel));
            }
            if (devIdAttrs.HasMacAddresses) {
                i = 0;
                devIdAttrs.MacAddresses.ForEach((mac) => {
                    ++i;
                    name = Key.FormatName(KeyNames.Mac, i);
					this.Add(new KeyPair(name, mac.toString()));
				});
			}
            if (devIdAttrs.HasIpAddresses) {
                i = 0;
                devIdAttrs.IpAddresses.ForEach((ip) => {
                    ++i;
					name = Key.FormatName(KeyNames.Ip, i);
					this.Add(new KeyPair(name, ip.toString()));
				});
			}
            if (devIdAttrs.HasSerialNumbers) {
                i = 0;
                devIdAttrs.SerialNumbers.ForEach((serNum) => {
                    ++i;
					name = Key.FormatName(KeyNames.SerialNumber, i);
					this.Add(new KeyPair(name, serNum.toString()));
				});
			}
			if (devIdAttrs.HasNbName) {
				name = Key.FormatName(KeyNames.NbName, 1);
				this.Add(new KeyPair(name, devIdAttrs.NbName.NetBiosName));

				name = Key.FormatName(KeyNames.NbDomain, 1);
				this.Add(new KeyPair(name, devIdAttrs.NbName.NetBiosDomain));
			}
			if (devIdAttrs.HasSysName) {
                name = Key.FormatName(KeyNames.SysName, 1);
				this.Add(new KeyPair(name, devIdAttrs.SysName.SystemName));

                name = Key.FormatName(KeyNames.SysOid, 1);
				this.Add(new KeyPair(name, devIdAttrs.SysName.SystemOID));
			}
			if (devIdAttrs.HasHostName) {
                name = Key.FormatName(KeyNames.HostName, 1);
				this.Add(new KeyPair(name, devIdAttrs.HostName.HostName));
			}
		}
	}

	public Resolve(deviceSet: DeviceSet): Device {
		var device: Device = null;

		var devIdAttrs: DeviceIdentityAttrs = this.ExtractDeviceIdentityAttrs();

		// TODO - Allow caller to control the settings
		var settings = new NetworkDiffSettings();

		var matchingDevices: List<MatchingDevice> =
			NetworkDiffer.GetMatchingDevices(devIdAttrs, deviceSet, settings);

		let bestMatch: MatchingDevice = null;

		if (matchingDevices.Count > 0) {
			bestMatch = matchingDevices.Get(0);
			let highestScore: number = matchingDevices.Get(0).Score;
			for (let i = 1; i < matchingDevices.Count; ++i)
			{
				if (matchingDevices.Get(i).Score == highestScore) {
					// Multiple matches have highest score, so resolution is ambiguous
					bestMatch = null;
				}
				else if (matchingDevices.Get(i).Score > highestScore) {
					bestMatch = matchingDevices.Get(i);
					highestScore = matchingDevices.Get(i).Score;
				}
			}
		}

		if (bestMatch != null) {
			return bestMatch.Device;
		}
		else {
			return null;
		}
		//if (matchingDevices.Count != 1)
        //    return null;
		//
        //return matchingDevices.Get(0).Device;
	}

	private ExtractDeviceIdentityAttrs(): DeviceIdentityAttrs {
		var result = new DeviceIdentityAttrs()

		var i = 0;
        var name: string = null;

        name = Key.FormatName(KeyNames.ControllerLabel, 1);
        var controllerLabel: string = this.ExtractString(name);
        if (controllerLabel != null) {
            // Reconstruct the controller key
            var controllerKey = new DeviceKey();
            this.ForEach((pair) => {
                // Look for controller key attributes
                if (StringUtils.StartsWith(pair.Name, DeviceKey.ControllerKeyPrefix)) {
                    // Remove controller key prefix from attribute name
                    var convertedName: string = pair.Name.substring(DeviceKey.ControllerKeyPrefix.length);
                    var value: string = pair.Value;

                    // Add attribute to controller key
                    controllerKey.Add(new KeyPair(convertedName, value));
                }
            }, this);

            // Populate dependent device id
            result.DependentDeviceId = new DependentDeviceId(null, controllerKey, controllerLabel);
        }

		i = 0;
		while (true) {
			++i;
            name = Key.FormatName(KeyNames.Mac, i);
			var mac: MacAddress = this.ExtractMacAddress(name);
			if (mac != null)
				result.MacAddresses.Add(mac);
			else
				break;
		}

		i = 0;
		while (true) {
			++i;
            name = Key.FormatName(KeyNames.Ip, i);
			var ip: IpAddress = this.ExtractIpAddress(name);
			if (ip != null)
				result.IpAddresses.Add(ip);
			else
				break;
		}

		i = 0;
		while (true) {
			++i;
            name = Key.FormatName(KeyNames.SerialNumber, i);
			var serNumStr: string = this.ExtractString(name);
			if (serNumStr != null)
				result.SerialNumbers.Add(new SerialNumberInfo(serNumStr));
			else
				break;
		}

        name = Key.FormatName(KeyNames.NbName, 1);
		var nbName: string = this.ExtractString(name);
		if (nbName != null) {
            name = Key.FormatName(KeyNames.NbDomain, 1);
			var nbDomain: string = this.ExtractString(name);

			result.NbName = new NetBiosNameInfo(nbName, nbDomain);
		}

        name = Key.FormatName(KeyNames.SysName, 1);
		var sysName: string = this.ExtractString(name);
		if (sysName != null) {
            name = Key.FormatName(KeyNames.SysOid, 1);
			var sysOid: string = this.ExtractString(name);

			result.SysName = new SystemNameInfo(sysName, sysOid);
		}

        name = Key.FormatName(KeyNames.HostName, 1);
		var hostName: string = this.ExtractString(name);
		if (hostName != null) {
			result.HostName = new HostNameInfo(hostName);
		}

		return result;
	}

	private ExtractMacAddress(keyName: string): MacAddress {
		var mac: MacAddress = null;
		var macPair: KeyPair = this.Find(keyName);
		if (macPair != null)
			mac = MacAddress.CreateFromString(macPair.Value);
		return mac;
	}

	private ExtractIpAddress(keyName: string): IpAddress {
		var ip: IpAddress = null;
		var ipPair: KeyPair = this.Find(keyName);
		if (ipPair != null)
			ip = IpAddress.Create(ipPair.Value);
		return ip;
	}

	private ExtractString(keyName: string): string {
		var str: string = null;
		var strPair: KeyPair = this.Find(keyName);
		if (strPair != null)
			str = strPair.Value;
		return str;
	}

	public Write(obj: any, context: any): void {
		this._Write(obj, "device_key", context);
	}

	public Read(obj: any, context: any): void {
		this._Read(obj, "device_key", context);
	}
}

export class IfKey extends Key implements ObjectSerializer {

	public static Create(device: Device, ifEntry: IfEntry): IfKey {
		return new IfKey(device, ifEntry);
    }

    public static CreateFromKey(other: Key): IfKey {
        var result = new IfKey();
        result.Copy(other);
        return result;
    }

	constructor(device?: Device, ifEntry?: IfEntry) {
        super();

        if (device)
			this.Copy(DeviceKey.Create(device));

        if (ifEntry)
			this.Add(new KeyPair(KeyNames.IfIndex, ifEntry.IfIndex.toString()));
    }

    //public static CreateFromVirtualPort(vPort: VirtualPort): IfKey {
    //    if (vPort == null)
    //        return null;

    //    var ifKey = new IfKey();
    //    ifKey.Add(new KeyPair(KeyNames.IfName, vPort.Key));

    //    return ifKey;
    //}

    //public static CreateFromPhysicalNic(physNic: PhysicalNic): IfKey {
    //    if (physNic == null)
    //        return null;

    //    var ifKey = new IfKey();
    //    ifKey.Add(new KeyPair(KeyNames.IfName, physNic.Device));

    //    return ifKey;
    //}

    //public static CreateFromVirtualNic(virtualNic: VirtualNic): IfKey {
    //    if (virtualNic == null)
    //        return null;

    //    var ifKey = new IfKey();
    //    ifKey.Add(new KeyPair(KeyNames.IfName, virtualNic.Device));

    //    return ifKey;
    //}

	public get IfIndex(): number {
		var result = -1;
		var ifIndexPair: KeyPair = this.Find(KeyNames.IfIndex);
		if (ifIndexPair != null)
			result = Number(ifIndexPair.Value);
		return result;
    }

    public get IfName(): string {
        var result: string = "";

        var ifNamePair: KeyPair = this.Find(KeyNames.IfName);
        if (ifNamePair != null) {
            if (!StringUtils.IsNullOrEmpty(ifNamePair.Value))
                result = ifNamePair.Value;
        }
        return result;
    }

	public Resolve(deviceSet: DeviceSet): ResolveResult {
		var device:Device = null;
		var ifEntry:IfEntry = null;

		var deviceKey = DeviceKey.CreateFromKey(this);

		var tmpDevice: Device = null;
		var tmpDevice = deviceKey.Resolve(deviceSet);
		if (!tmpDevice)
			return new ResolveResult(false);

		if (!tmpDevice.HasIfTable)
			return new ResolveResult(false);

        var ifIndexPair: KeyPair = null;
        var ifNamePair: KeyPair = null;
        var tmpIfEntry: IfEntry = null;

        ifIndexPair = this.Find(KeyNames.IfIndex);
        if (ifIndexPair == null)
            ifNamePair = this.Find(KeyNames.IfName);

        if ((ifIndexPair == null) && (ifNamePair == null))
            return new ResolveResult(false);

        if (ifIndexPair != null) {
            var ifIndex: number = parseInt(ifIndexPair.Value);
            if (isNaN(ifIndex))
                return new ResolveResult(false);

            tmpIfEntry = tmpDevice.IfTable.Find(ifIndex);
            if (tmpIfEntry == null)
                return new ResolveResult(false);
        }
        else if (ifNamePair != null) {
            var ifName: string = ifNamePair.Value;

            tmpIfEntry = tmpDevice.IfTable.FindByName(ifName);
            if (tmpIfEntry == null)
                return new ResolveResult(false);
        }

        device = tmpDevice;
        ifEntry = tmpIfEntry;

		return new ResolveResult(true, device, ifEntry);
	}

	public Write(obj: any, context: any): void {
		this._Write(obj, "if_key", context);
	}

	public Read(obj: any, context: any): void {
		this._Read(obj, "if_key", context);
	}
}

export class ResolveResult {
	constructor(
		public Result: boolean,
		public Device?: Device,
		public IfEntry?: IfEntry) {
	}
}
